// Template editor with live mustache preview + merge tag picker.
// Sample context fills in placeholder values for the preview.

import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTemplate, saveTemplate } from "@/lib/api";

const MERGE_TAGS = [
  "voornaam",
  "achternaam",
  "naam",
  "bedrijf",
  "email",
  "aantal_tickets",
  "bestelnr",
  "qr_link",
  "event_datum",
  "event_tijd",
  "event_locatie",
  "event_short",
  "unsubscribe_link",
  "korting_code",
  "factuurnr",
  "bedrag",
  "vervaldatum",
];

const SAMPLE_CTX: Record<string, string> = {
  voornaam: "Joost",
  achternaam: "van der Berg",
  naam: "Joost van der Berg",
  bedrijf: "Voorbeeld BV",
  email: "joost@example.com",
  aantal_tickets: "2",
  bestelnr: "HP26-000123",
  qr_link: "https://odeaandeharing.nl/ticket.html?o=HP26-000123&t=...",
  event_datum: "donderdag 18 juni 2026",
  event_tijd: "17:00",
  event_locatie: "Café Dokus, Sonseweg 14, Best",
  event_short: "Haringparty 2026",
  korting_code: "AMB-X4Y9",
  factuurnr: "2026-0094",
  bedrag: "950,00",
  vervaldatum: "2 mei 2026",
  contact_naam: "Joost van der Berg",
  iban: "NL00 RABO 0000 0000 00",
  tenaamstelling: "Stichting Haringparty Best",
  unsubscribe_link: "mailto:tickets@odeaandeharing.nl?subject=Uitschrijven",
};

function render(template: string, ctx: Record<string, string>): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => ctx[k] ?? "");
}

export function TemplateEdit() {
  const { slug } = useParams<{ slug: string }>();
  const qc = useQueryClient();

  const { data: tpl, isLoading } = useQuery({
    queryKey: ["template", slug],
    queryFn: () => getTemplate(slug!),
    enabled: !!slug,
  });

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [trigger, setTrigger] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (tpl) {
      setName(tpl.name);
      setSubject(tpl.subject);
      setHtml(tpl.html);
      setTrigger(tpl.trigger);
      setActive(tpl.active);
    }
  }, [tpl]);

  const mut = useMutation({
    mutationFn: () => saveTemplate({ id: tpl!.id, name, subject, html, trigger, active }),
    onSuccess: () => {
      setToast("✓ Opgeslagen");
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.invalidateQueries({ queryKey: ["template", slug] });
      setTimeout(() => setToast(null), 2000);
    },
  });

  const renderedSubject = useMemo(() => render(subject, SAMPLE_CTX), [subject]);
  const renderedHtml = useMemo(() => render(html, SAMPLE_CTX), [html]);

  if (isLoading || !tpl) return <div className="empty"><span className="spinner" /> Laden…</div>;

  return (
    <>
      <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <Link to="/templates" className="pill">← Terug</Link>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8rem" }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Actief
          </label>
          <button className="btn-admin sm" onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Opslaan…" : "💾 Opslaan"}
          </button>
        </div>
      </div>

      <div className="row2">
        <div className="panel">
          <div className="panel-h">
            <h3>Bewerken</h3>
            <code style={{ fontFamily: "var(--f-mono)", fontSize: "0.78rem", color: "var(--c-orange)" }}>{tpl.slug}</code>
          </div>

          <div className="field">
            <label>Naam</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field">
            <label>Trigger</label>
            <select value={trigger ?? ""} onChange={(e) => setTrigger(e.target.value || null)}>
              <option value="">— geen / handmatig —</option>
              <option value="order.paid">Na betaling (order.paid)</option>
              <option value="t-14">T-14 reminder</option>
              <option value="t-1">T-1 reminder</option>
              <option value="post_event">Na het event</option>
              <option value="manual">Handmatig</option>
            </select>
          </div>

          <div className="field">
            <label>Onderwerp</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div className="field">
            <label>Merge tags · klik om te plakken</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.7rem" }}>
              {MERGE_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="pill"
                  onClick={() => navigator.clipboard?.writeText(`{{ ${t} }}`)}
                >
                  {`{{ ${t} }}`}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>HTML</label>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} style={{ minHeight: "400px", fontFamily: "var(--f-mono)", fontSize: "0.82rem" }} />
          </div>
        </div>

        <div>
          <div className="panel">
            <div className="panel-h"><h3>Live preview</h3></div>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.75rem", color: "var(--c-orange)", marginBottom: "0.4rem" }}>
              ONDERWERP
            </div>
            <div style={{ marginBottom: "1rem", color: "var(--c-cream)", fontWeight: 600 }}>{renderedSubject}</div>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.75rem", color: "var(--c-orange)", marginBottom: "0.4rem" }}>
              BODY (HTML, sample data)
            </div>
            <iframe
              title="preview"
              srcDoc={renderedHtml}
              style={{ width: "100%", minHeight: "500px", border: "1px solid rgba(255,122,26,0.3)", background: "#fff" }}
            />
          </div>
        </div>
      </div>

      {toast && <div className="toast success">{toast}</div>}
    </>
  );
}
